import { QueryFactory } from '@lmt/shared-types';
import {
  CreateHL7FilterDto,
  HL7FilterAction,
  HL7FilterDto,
  UpdateHL7FilterDto,
} from '@lmt/shared-types';

import { Error } from '@lmt/unicorn-shared/app/components/common/Error';
import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import { FilterBuilderLazy } from '@lmt/unicorn-shared/app/components/common/LazyWrappers/FilterBuilderLazy';
import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { SubmitButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/SubmitButton';
import {
  DropDownInput,
  HookFormDropDownInput,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/DropDownInput';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import { requiredRule } from '@lmt/unicorn-shared/app/helpers/form/react-hook-form-helper';
import {
  convertToErrorString,
  useErrorHandler,
} from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import clsx from 'clsx';
import { HL7MessageSegments } from '@lmt/unicorn-shared/data/messageSegmentNames';
import { HL7MessageTypeNames } from '@lmt/unicorn-shared/data/messageTypeNames';
import type { dxFilterBuilderField } from 'devextreme/ui/filter_builder';
import React, { useCallback, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import filterStyles from '@lmt/unicorn-shared/app/components/calendar/color-settings/form/ColorRuleEditForm.module.scss';
import styles from './HL7FilterEditForm.module.scss';
import globalFormStyles from '@lmt/unicorn-shared/app/components/global-forms/Content.module.scss';

export type Props = {
  filterId?: number;
  formId: number;
};

type Form = Omit<
  HL7FilterDto,
  'position' | 'id' | 'enabled' | 'filterExpression'
>;
const groupOperations: Array<'and' | 'or' | 'notAnd' | 'notOr'> = ['and', 'or'];
export const HL7FilterEditForm: React.FC<Props> = React.memo(
  function FilterBuilderWrapper(props) {
    const i18next = useScopedTranslation('hl7.filters');
    const globalForms = useGlobalForms();
    const filterQuery = QueryFactory.HL7FilterQuery.useGetAllQuery();
    const filter = useMemo(() => {
      return filterQuery.data?.find((x) => x.id === props.filterId) ?? null;
    }, [filterQuery.data]);

    const [parsedFilter, setParsedFilter] = useState(() =>
      filter ? JSON.parse(filter.filterExpression) : undefined,
    );
    const handleFiltersChanged = useCallback((filter: any) => {
      setParsedFilter(filter.value);
    }, []);

    const form = useForm<Form>({
      defaultValues: {
        ...filter,
      },
    });
    const queryClient = useQueryClient();
    const dxFields: dxFilterBuilderField[] = useMemo(() => {
      return HL7MessageSegments.map((x) => {
        return {
          dataType: 'string',
          dataField: x.id,
          caption: `${x.id} - ${x.description}`,
        } as dxFilterBuilderField;
      });
    }, []);
    const [isSaving, setIsSaving] = useState(false);
    const onSave = useErrorHandler(async (data: Form) => {
      setIsSaving(true);
      setDeleteError(undefined);

      try {
        if (filter?.id) {
          await QueryFactory.HL7FilterClient.patch(filter.id, {
            ...data,
            messageType: data.messageType!,
            filterExpression: parsedFilter
              ? JSON.stringify(parsedFilter)
              : null,
          });
        } else {
          await QueryFactory.HL7FilterClient.create({
            action: data.action,
            enabled: true,
            filterExpression: parsedFilter
              ? JSON.stringify(parsedFilter)
              : null,
            messageType: data.messageType!,
          });
        }
        await queryClient.invalidateQueries({
          queryKey: QueryFactory.HL7FilterQuery.getAllQueryKey(),
        });
        globalForms.closeForm(props.formId);
      } finally {
        setIsSaving(false);
      }
    }, form.setError);

    const [deleteError, setDeleteError] = useState<string | undefined>();
    const [isDeleting, setIsDeleting] = useState(false);
    const deleteFilter = useCallback(async () => {
      if (!props.filterId) return;
      setDeleteError(undefined);
      setIsDeleting(true);
      try {
        await QueryFactory.HL7FilterClient.remove(props.filterId);
        await queryClient.invalidateQueries({
          queryKey: QueryFactory.HL7FilterQuery.getAllQueryKey(),
        });
        globalForms.closeForm(props.formId);
      } catch (e) {
        setDeleteError(convertToErrorString(e));
      } finally {
        setIsDeleting(false);
      }
    }, []);

    return (
      <div className={clsx('globalFormContentRoot', styles.root)}>
        <form onSubmit={form.handleSubmit(onSave.handler)}>
          <Loading
            loading={isSaving || isDeleting}
            title={
              isDeleting
                ? i18next.t('deleting_filter')
                : isSaving
                  ? i18next.t('saving_filters')
                  : ''
            }
          >
            <div className={globalFormStyles.dataContainer}>
              <Field title={i18next.t('message_type')} required={true}>
                <Controller
                  control={form.control}
                  rules={requiredRule()}
                  name={'messageType'}
                  render={({ field: { onChange, value } }) => (
                    <DropDownInput
                      useIdFunctionAsValue={true}
                      required
                      variant="formInput"
                      getOptionLabel="description"
                      idFunction="id"
                      errorText={form.formState.errors.messageType?.message}
                      options={HL7MessageTypeNames}
                      value={value}
                      onValueChanged={(newValue) => {
                        onChange(newValue.id);
                      }}
                    />
                  )}
                />
              </Field>

              <Field
                title={i18next.t('action')}
                required={true}
                className={globalFormStyles.field}
              >
                <HookFormDropDownInput
                  control={form.control}
                  name={'action'}
                  rules={requiredRule()}
                  required
                  variant="formInput"
                  getOptionLabel="description"
                  idFunction="id"
                  errorText={form.formState.errors.action?.message}
                  options={[
                    {
                      id: HL7FilterAction.Accept,
                      description: i18next.t('accept'),
                    },
                    {
                      id: HL7FilterAction.Reject,
                      description: i18next.t('reject'),
                    },
                  ]}
                />
              </Field>

              <Field
                title={i18next.t('filter')}
                className={globalFormStyles.field}
              >
                <FilterBuilderLazy
                  className={filterStyles.filter}
                  fields={dxFields}
                  allowHierarchicalFields={true}
                  groupOperations={groupOperations}
                  maxGroupLevel={1}
                  onValueChanged={handleFiltersChanged}
                  defaultValue={
                    parsedFilter
                  } /* we can't save custom objects in ReactForm, so we store filters outside of it */
                />
              </Field>
            </div>
            <div className={globalFormStyles.submitContainer}>
              <Error error={deleteError ?? onSave.overallServerError} />
              {props.filterId && (
                <Button
                  onClick={deleteFilter}
                  color={ButtonColor.Danger}
                  className={globalFormStyles.submit}
                  title={i18next.t('delete_button')}
                />
              )}
              <SubmitButton
                submitting={form.formState.isSubmitting}
                className={globalFormStyles.submit}
                title={i18next.t('save_button')}
              />
            </div>
          </Loading>
        </form>
      </div>
    );
  },
);
